<template>
  <pie-chart-card
    class="properties-required-card"
    :title="$t('goalInsightsHealth.propertiesRequired.cardTitle')"
    :loading="loading"
    :chart-data="totalValue === 0 ? [] : data"
    clickable
    @click="handler($event)"
  >
    <template #header>
      <dashboard-card-header
        :title="$t('goalInsightsHealth.propertiesRequired.cardTitle')"
        text-key="goalInsightsHealth.propertiesRequired.cardHelp"
        :can-edit="canEdit"
        :link="settingsLink"
        :loading="loading"
      />
    </template>
  </pie-chart-card>
</template>

<script>
import DashboardCardHeader from '@/components/goal-insights/health/DashboardCardHeader.vue';
import PieChartCard from '@/components/dashboard/PieChartCard.vue';
import useAccess from '@/composables/access/access';
import useDebounce from 'shared/composables/debounce';
import useGoalTypeProperty from '@/composables/customize-page/goal-type-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useProperties from '@/composables/property/property';
import usePublishedAtFilter from '@/composables/goal/published-at-filter';
import useSubscription from '@/composables/subscription/subscription';
import { AND } from 'shared/api/query/filter';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { accessGroupFlag, routeName } from 'shared/constants.json';
import { applyBaseFilter } from '@/lib/filter/base-filter';
import { computed } from 'vue';
import { dogma } from '@/api';
import { goalTypesFilter } from '@/lib/filter/goal/goals-type-filter';
import { propertiesRequiredFilter } from '@/lib/filter/goal/properties-required-filter';
import { queryPropertiesRequired } from '@/api/query/goal-insights-health';
import { textByLang } from 'shared/lib/language';

export default {
  name: 'PropertiesRequiredCard',
  props: {
    selectedCycles: {
      type: Array,
      required: true,
    },
    viewFilter: {
      type: Object,
      required: true,
    },
    goalType: {
      type: Object,
      required: true,
    },
  },
  emits: ['show-goal-list', 'show'],
  setup(props) {
    const { userLang } = useLoggedInUser();
    const { debounce } = useDebounce();
    const { subscribe } = useSubscription();
    const { properties } = useProperties();

    const goalTypeWithRequiredProperties = computed(() => ({
      ...props.goalType,
      requiredProperties: props.goalType.requiredProperties.map((rp) => properties.value.find((prop) => prop.uid === rp.uid)),
    }));

    const { goalTypeProperty } = useGoalTypeProperty();
    const { publishedAtFilter } = usePublishedAtFilter();
    const { userHasRight } = useAccess();
    return {
      debounce,
      subscribe,

      goalTypeWithRequiredProperties,

      userLang,
      goalTypeProperty,
      publishedAtFilter,
      userHasRight,
    };
  },
  components: { DashboardCardHeader, PieChartCard },
  data() {
    return {
      loading: false,
      data: [
        { key: 'right', label: this.$t('goalInsightsHealth.propertiesRequired.labelRight'), value: 0, series: [], color: 'green' },
        { key: 'wrong', label: this.$t('goalInsightsHealth.propertiesRequired.labelWrong'), value: 0, series: [], color: 'red' },
      ],
    };
  },
  computed: {
    series() {
      return this.data.map((e) => e.series).flat().map((e) => e.uid);
    },
    totalValue() {
      return this.data.reduce((acc, e) => acc + e.value, 0);
    },
    canEdit() {
      return this.userHasRight([accessGroupFlag.goalCycleWriteAccess]);
    },
    settingsLink() {
      return { name: routeName.goalTypes, params: { optionId: this.goalType.uid } };
    },
    gqlFilterObject() {
      const handler = new GoalFilterHandler();
      return handler.Translate(this.viewFilter);
    },
    gqlFilter() {
      return this.gqlFilterObject.filterTree;
    },
  },
  methods: {
    handler(bucket) {
      const bucketFilter = [];
      switch (bucket.key) {
        case 'wrong':
          bucketFilter.push(propertiesRequiredFilter(this.goalTypeWithRequiredProperties));
          break;
        case 'right':
        default:
          bucketFilter.push(propertiesRequiredFilter(this.goalTypeWithRequiredProperties, true));
          break;
      }

      this.$emit('show-goal-list', {
        filter: {
          op: AND,
          children: [
            this.viewFilter,
            goalTypesFilter(this.goalTypeProperty, this.goalType),
            ...bucketFilter,
          ],
        },
        title: this.$t('goalInsightsHealth.propertiesRequired.dialogTitle', { label: bucket.label, goalType: textByLang(this.goalType.label, this.userLang) }),
      });
    },
    retrievePropertiesRequiredRestriction() {
      if (this.goalType.descriptionRequired === false
          && this.goalType.cycleRequired === false
          && this.goalType.requiredProperties.length === 0
      ) {
        this.$emit('show', false);
        return;
      }
      this.$emit('show', true);

      this.loading = true;
      dogma.query(applyBaseFilter({
        queries: [
          ...queryPropertiesRequired({
            goalCycles: this.selectedCycles,
            filter: this.gqlFilter,
            varBlocks: this.gqlFilterObject.varBlocks,
            goalTypeOption: this.goalTypeWithRequiredProperties,
          }),
        ],
        operator: AND,
        baseFilter: this.publishedAtFilter,
      })).then((response) => {
        this.loading = false;
        if (response.status !== 200) {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
          return;
        }

        this.data.forEach((e) => {
          switch (e.key) {
            case 'right':
            case 'wrong':
              e.series = response.data[e.key];
              e.value = e.series.length;
              break;
            default:
              break;
          }
        });
      });
    },
    fetch(wait) {
      this.debounce(this.retrievePropertiesRequiredRestriction, wait, 'retrievePropertiesRequiredRestriction');
    },
  },
  watch: {
    selectedCycles(val, oldVal) {
      if (JSON.stringify(val) === JSON.stringify(oldVal)) {
        return;
      }
      this.fetch(1000);
    },
    gqlFilterObject: {
      handler(val, oldVal) {
        if (JSON.stringify(val) === JSON.stringify(oldVal)) {
          return;
        }
        this.fetch(1000);
      },
      deep: true,
    },
  },
  created() {
    this.fetch(0);

    const fn = this.fetch;
    this.subscribe({
      queryFn: Promise.resolve(),
      syncFn({ isSelfEvent }) {
        const delay = isSelfEvent ? 1000 : 30000;
        fn(delay);
      },
      model: 'goal',
      includeSelfEvents: true,
    });
  },
};
</script>
